import { closestCenter, DndContext, type DragEndEvent } from "@dnd-kit/core";
import {
	arrayMove,
	SortableContext,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { ColumnOrderState, VisibilityState } from "@tanstack/react-table";
import { GripVertical, Settings2 } from "lucide-react";
import { useMemo } from "react";
import { Button } from "@/v2/components/ui/button";
import { Checkbox } from "@/v2/components/ui/checkbox";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/v2/components/ui/popover";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/v2/components/ui/tooltip";
import { cn } from "@/v2/lib/utils";

interface ColumnsMenuProps {
	columnVisibility: VisibilityState;
	onColumnVisibilityChange: (next: VisibilityState) => void;
	columnOrder: ColumnOrderState;
	onColumnOrderChange: (next: ColumnOrderState) => void;
	/** Display labels for the user-reorderable columns, keyed by column id. */
	labels: Record<string, string>;
	/** Canonical order of the reorderable columns. */
	defaultOrder: ColumnOrderState;
	/** Default visibility map for the "Reset to default" affordance. */
	defaultVisibility: VisibilityState;
}

/**
 * Floating column manager — toggle + drag-reorder the user-facing columns. Mode
 * specifics (labels, default order/visibility) are passed in so the same menu
 * serves both the normal Entries and Auto-combine tables.
 */
export function ColumnsMenu({
	columnVisibility,
	onColumnVisibilityChange,
	columnOrder,
	onColumnOrderChange,
	labels,
	defaultOrder,
	defaultVisibility,
}: ColumnsMenuProps) {
	const isVisible = (key: string) => columnVisibility[key] !== false;
	const isDefaultVisible = (key: string) => defaultVisibility[key] !== false;
	const orderedColumnIds = useMemo(() => {
		const allowed = new Set(defaultOrder);
		const seen = new Set<string>();
		const out: string[] = [];
		for (const id of columnOrder) {
			if (!allowed.has(id) || seen.has(id)) continue;
			seen.add(id);
			out.push(id);
		}
		for (const id of defaultOrder) {
			if (!seen.has(id)) out.push(id);
		}
		return out;
	}, [columnOrder, defaultOrder]);
	const toggle = (key: string) =>
		onColumnVisibilityChange({ ...columnVisibility, [key]: !isVisible(key) });
	const handleDragEnd = ({ active, over }: DragEndEvent) => {
		if (!over || active.id === over.id) return;
		const oldIndex = orderedColumnIds.indexOf(String(active.id));
		const newIndex = orderedColumnIds.indexOf(String(over.id));
		if (oldIndex < 0 || newIndex < 0) return;
		onColumnOrderChange(arrayMove(orderedColumnIds, oldIndex, newIndex));
	};
	const hasCustomColumns =
		Object.keys(labels).some(
			(key) => isVisible(key) !== isDefaultVisible(key),
		) || orderedColumnIds.join(",") !== defaultOrder.join(",");

	return (
		<Popover>
			<Tooltip>
				<TooltipTrigger asChild>
					<PopoverTrigger asChild>
						<Button
							variant="ghost"
							size="icon"
							aria-label="Columns"
							className="relative size-7 bg-muted text-muted-foreground hover:bg-muted hover:text-foreground"
						>
							<Settings2 className="size-4" />
							{hasCustomColumns && (
								<span className="absolute top-1 right-1 size-1.5 rounded-full bg-ring" />
							)}
						</Button>
					</PopoverTrigger>
				</TooltipTrigger>
				<TooltipContent side="bottom">Columns</TooltipContent>
			</Tooltip>
			<PopoverContent align="end" className="w-56 p-2">
				<div className="px-2 pb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
					Columns
				</div>
				<DndContext
					collisionDetection={closestCenter}
					onDragEnd={handleDragEnd}
				>
					<SortableContext
						items={orderedColumnIds}
						strategy={verticalListSortingStrategy}
					>
						<div className="flex flex-col">
							{orderedColumnIds.map((key) => (
								<ColumnMenuRow
									key={key}
									id={key}
									label={labels[key] ?? key}
									checked={isVisible(key)}
									onToggle={() => toggle(key)}
								/>
							))}
						</div>
					</SortableContext>
				</DndContext>
				{hasCustomColumns && (
					<div className="mt-1 border-t border-border pt-1">
						<Button
							variant="ghost"
							size="sm"
							onClick={() => {
								onColumnVisibilityChange(defaultVisibility);
								onColumnOrderChange(defaultOrder);
							}}
							className="h-7 w-full text-xs text-muted-foreground hover:text-foreground"
						>
							Reset to default
						</Button>
					</div>
				)}
			</PopoverContent>
		</Popover>
	);
}

function ColumnMenuRow({
	id,
	label,
	checked,
	onToggle,
}: {
	id: string;
	label: string;
	checked: boolean;
	onToggle: () => void;
}) {
	const {
		attributes,
		listeners,
		setActivatorNodeRef,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id });

	return (
		<div
			ref={setNodeRef}
			style={{
				transform: CSS.Transform.toString(transform),
				transition,
			}}
			className={cn("flex items-center rounded-sm", isDragging && "z-10")}
		>
			<Button
				variant="ghost"
				size="sm"
				onClick={onToggle}
				className="h-8 min-w-0 flex-1 justify-start gap-2 rounded-sm px-2 text-xs font-normal hover:bg-accent hover:text-accent-foreground"
			>
				<Checkbox
					checked={checked}
					tabIndex={-1}
					aria-hidden
					className="pointer-events-none shrink-0"
				/>
				<span className="truncate">{label}</span>
			</Button>
			<button
				type="button"
				ref={setActivatorNodeRef}
				aria-label={`Reorder ${label} column`}
				className="flex h-8 w-7 cursor-grab items-center justify-center rounded-sm text-muted-foreground hover:bg-accent hover:text-foreground active:cursor-grabbing"
				{...attributes}
				{...listeners}
			>
				<GripVertical className="size-3.5" />
			</button>
		</div>
	);
}
